import posthog from "posthog-js";
import { sanitizeAppRoute } from "@/utils/sanitizeAppRoute";

export const sanitizePostHogRoute = sanitizeAppRoute;

type PostHogEnv = Partial<Record<"VITE_PUBLIC_POSTHOG_KEY" | "VITE_PUBLIC_POSTHOG_HOST" | "MODE", string>>;
type PostHogUser = {
  id?: string | null;
  email?: string | null;
};

let initialized = false;
let identifiedUserSignature: string | null = null;
let identifiedUserId: string | null = null;

export type PostHogAppArea = "coach" | "leader" | "preview" | "public" | "internal";

type PostHogCaptureEvent = {
  properties?: Record<string, unknown>;
};

function missingPostHogEnv(env: PostHogEnv) {
  const missing: string[] = [];
  if (!env.VITE_PUBLIC_POSTHOG_KEY?.trim()) missing.push("VITE_PUBLIC_POSTHOG_KEY");
  if (!env.VITE_PUBLIC_POSTHOG_HOST?.trim()) missing.push("VITE_PUBLIC_POSTHOG_HOST");
  return missing;
}

export function getPostHogConfig(env: PostHogEnv) {
  const key = env.VITE_PUBLIC_POSTHOG_KEY?.trim();
  const host = env.VITE_PUBLIC_POSTHOG_HOST?.trim();

  if (!key || !host) {
    return null;
  }

  return {
    key,
    host,
  };
}

export function initPostHog(env: PostHogEnv = import.meta.env) {
  if (initialized) return;

  const config = getPostHogConfig(env);
  if (!config) {
    if (env.MODE !== "production") {
      const missing = missingPostHogEnv(env).join(", ");
      console.warn(`[PostHog] Analytics disabled. Missing ${missing}. Set Vite public PostHog env vars before building.`);
    }
    return;
  }

  posthog.init(config.key, {
    api_host: config.host,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    advanced_disable_decide: true,
    advanced_disable_feature_flags: true,
    save_campaign_params: false,
    save_referrer: false,
    property_denylist: [
      "$current_url",
      "$pathname",
      "$session_entry_url",
      "$session_entry_pathname",
      "$initial_current_url",
      "$initial_pathname",
    ],
    persistence: "localStorage+cookie",
    before_send: sanitizePostHogCaptureEvent,
    loaded(instance) {
      if (env.MODE !== "production") {
        instance.opt_in_capturing();
      }
    },
  });

  initialized = true;
}

function stripQueryAndHash(pathname: string) {
  return pathname.split(/[?#]/)[0] || "/";
}

function sanitizePostHogUrl(value: unknown) {
  if (typeof value !== "string") return value;

  try {
    const url = new URL(value);
    url.pathname = sanitizePostHogRoute(url.pathname);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return sanitizePostHogRoute(value);
  }
}

export function sanitizePostHogCaptureEvent(event: PostHogCaptureEvent | null) {
  if (!event?.properties) return event;

  const properties = { ...event.properties };
  if ("$current_url" in properties) properties.$current_url = sanitizePostHogUrl(properties.$current_url);
  if ("$pathname" in properties) properties.$pathname = sanitizePostHogRoute(String(properties.$pathname || ""));
  if ("$session_entry_url" in properties) properties.$session_entry_url = sanitizePostHogUrl(properties.$session_entry_url);
  if ("$session_entry_pathname" in properties) {
    properties.$session_entry_pathname = sanitizePostHogRoute(String(properties.$session_entry_pathname || ""));
  }
  if ("$initial_current_url" in properties) properties.$initial_current_url = sanitizePostHogUrl(properties.$initial_current_url);
  if ("$initial_pathname" in properties) properties.$initial_pathname = sanitizePostHogRoute(String(properties.$initial_pathname || ""));

  return {
    ...event,
    properties,
  };
}

export function resolvePostHogAppArea(pathname: string): PostHogAppArea {
  const route = stripQueryAndHash(pathname.trim());
  if (route.startsWith("/coach")) return "coach";
  if (route.startsWith("/preview")) return "preview";
  if (route.startsWith("/internal")) return "internal";
  if (
    route.startsWith("/today")
    || route.startsWith("/shared-memory")
    || route.startsWith("/moments")
    || route.startsWith("/leader")
    || route.startsWith("/insights")
    || route.startsWith("/veera")
    || route.startsWith("/settings")
    || route.startsWith("/apps")
    || route.startsWith("/reflect")
    || route.startsWith("/transcript")
    || route.startsWith("/post-meeting")
    || route.startsWith("/meetings")
    || route.startsWith("/onboarding")
  ) {
    return "leader";
  }

  return "public";
}

export function capturePostHogRouteView(pathname: string, appArea: PostHogAppArea = resolvePostHogAppArea(pathname)) {
  if (!initialized) return;

  posthog.capture("app_route_viewed", {
    app_area: appArea,
    route: sanitizePostHogRoute(pathname),
  });
}

export function capturePostHogEvent(eventName: string, properties: Record<string, unknown> = {}) {
  if (!initialized) return;

  posthog.capture(eventName, properties);
}

export function identifyPostHogUser(user: PostHogUser | null | undefined, roles: string[] = []) {
  const userId = user?.id?.trim();
  if (!initialized || !userId) return;

  const normalizedRoles = Array.from(new Set(roles.filter(Boolean))).sort();
  const email = user.email?.trim().toLowerCase() || undefined;
  const signature = JSON.stringify({ userId, email, roles: normalizedRoles });

  if (identifiedUserSignature === signature) return;

  if (identifiedUserId && identifiedUserId !== userId) {
    posthog.reset();
  }

  posthog.identify(userId, {
    ...(email ? { email } : {}),
    ...(normalizedRoles.length > 0
      ? {
          app_roles: normalizedRoles,
          primary_app_role: normalizedRoles[0],
        }
      : {}),
  });
  identifiedUserSignature = signature;
  identifiedUserId = userId;
}

export function resetPostHogIdentity() {
  if (!initialized) return;

  posthog.reset();
  identifiedUserSignature = null;
  identifiedUserId = null;
}

export function resetPostHogForTests() {
  initialized = false;
  identifiedUserSignature = null;
  identifiedUserId = null;
}
